import React, { useState } from 'react'
import { C, Spinner, Empty, Mono } from './ui'
import { useFetch } from '../hooks/useFetch'
import { getGameDetail } from '../api/client'

function fmtAm(n) {
  if (n == null) return '—'
  return n > 0 ? `+${n}` : `${n}`
}

function formatET(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
    })
  } catch { return iso }
}

function formatFullDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long', month: 'long', day: 'numeric',
    })
  } catch { return '' }
}

function degToCompass(deg) {
  if (deg == null) return ''
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  return dirs[Math.round(deg / 45) % 8]
}

function computeImplied(ml, total) {
  const point = total?.point
  if (!point || ml?.home == null || ml?.away == null) return { home: null, away: null }
  const toProb = (o) => o < 0 ? -o / (-o + 100) : 100 / (o + 100)
  const hp = toProb(ml.home), ap = toProb(ml.away)
  const sum = hp + ap
  return {
    home: (point * hp / sum).toFixed(1),
    away: (point * ap / sum).toFixed(1),
  }
}

function Section({ title, children, right }) {
  return (
    <div style={{
      background: C.bk2, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: '12px 14px', margin: '8px 14px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${C.dim2}`,
      }}>
        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 14, color: C.text, letterSpacing: '.04em' }}>{title}</span>
        {right && <span style={{ fontSize: 9, color: C.dim }}>{right}</span>}
      </div>
      {children}
    </div>
  )
}

function SnapshotRow({ label, value, valueColor }) {
  if (value == null || value === '') return null
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '5px 0', borderTop: `1px solid ${C.dim2}`,
    }}>
      <span style={{ fontSize: 11, color: C.dim }}>{label}</span>
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 700, color: valueColor || C.text }}>
        {value}
      </span>
    </div>
  )
}

function GameSnapshotSection({ game }) {
  const ml  = game.odds?.ml    || {}
  const tot = game.odds?.total || {}
  const sp  = game.odds?.spread || {}
  const w   = game.weather || {}
  const implied = computeImplied(ml, tot)
  const lineup  = game.intel?.lineup_status

  const windRelLabel = {
    out_to_cf:  'Blowing Out ↑',
    in_from_cf: 'Blowing In ↓',
    crossing:   'Crossing →',
    dome:       'Dome',
  }[w.wind_relative] || ''

  const lineupColor = { Confirmed: C.green, Projected: C.yellow, TBD: C.dim, Missing: C.red }[lineup] || C.dim

  return (
    <Section title="Game Snapshot">
      {/* Date + time + venue */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>
          {formatFullDate(game.commence_time)}
          {' · '}
          <span style={{ color: C.blue }}>{formatET(game.commence_time)} ET</span>
        </div>
        {game.venue && (
          <div style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>🏟 {game.venue}</div>
        )}
      </div>

      {/* Weather block */}
      {(w.conditions || w.temp_f != null) && (
        <div style={{
          background: C.bk, borderRadius: 8, padding: '8px 10px',
          border: `1px solid ${C.dim2}`, marginBottom: 8,
        }}>
          {w.is_dome ? (
            <div style={{ fontSize: 11, color: C.dim }}>🏠 Dome — weather not applicable</div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: C.text, marginBottom: 4 }}>
                {w.conditions && <span>🌤 {w.conditions}</span>}
                {w.temp_f != null && <span style={{ marginLeft: 8 }}>🌡 {w.temp_f}°F</span>}
                {w.humidity_pct != null && <span style={{ marginLeft: 8, color: C.dim }}>{w.humidity_pct}% humidity</span>}
              </div>
              {(w.wind_mph != null || windRelLabel) && (
                <div style={{ fontSize: 11, color: C.dim }}>
                  💨 {w.wind_mph != null ? `${w.wind_mph} mph` : ''}
                  {w.wind_dir_deg != null && <span> {degToCompass(w.wind_dir_deg)}</span>}
                  {windRelLabel && <span style={{ color: w.wind_relative === 'out_to_cf' ? C.green : w.wind_relative === 'in_from_cf' ? C.red : C.dim }}>{' · '}{windRelLabel}</span>}
                </div>
              )}
              {w.precip_pct != null && w.precip_pct > 20 && (
                <div style={{ fontSize: 10, color: C.yellow, marginTop: 3 }}>☔ {w.precip_pct}% chance of rain</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Odds grid */}
      {(ml.home != null || tot.point != null) && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 1fr', gap: 4, marginBottom: 4 }}>
            <div />
            {['ML', 'TOTAL', 'RL'].map(l => (
              <div key={l} style={{ textAlign: 'center', fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>{l}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 1fr', gap: 4, marginBottom: 4, alignItems: 'center' }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim }}>{game.away?.abbreviation}</span>
            <OddsCell top={fmtAm(ml.away)} />
            <OddsCell top={tot.point != null ? `o${tot.point}` : '—'} bottom={fmtAm(tot.over)} />
            <OddsCell top={sp.away_pt != null ? (sp.away_pt > 0 ? `+${sp.away_pt}` : `${sp.away_pt}`) : '—'} bottom={fmtAm(sp.away)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 1fr 1fr', gap: 4, marginBottom: 8, alignItems: 'center' }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim }}>{game.home?.abbreviation}</span>
            <OddsCell top={fmtAm(ml.home)} />
            <OddsCell top={tot.point != null ? `u${tot.point}` : '—'} bottom={fmtAm(tot.under)} />
            <OddsCell top={sp.home_pt != null ? (sp.home_pt > 0 ? `+${sp.home_pt}` : `${sp.home_pt}`) : '—'} bottom={fmtAm(sp.home)} />
          </div>
        </>
      )}

      {/* Implied runs */}
      {(implied.home != null || implied.away != null) && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8,
          padding: '8px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center', marginBottom: 4,
        }}>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 9, color: C.dim, letterSpacing: '.06em', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>
              {game.away?.abbreviation} IMPLIED
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 18, color: C.text }}>
              {implied.away ?? '—'}
            </div>
          </div>
          <div style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>RUNS</div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, color: C.dim, letterSpacing: '.06em', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>
              {game.home?.abbreviation} IMPLIED
            </div>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 18, color: C.text }}>
              {implied.home ?? '—'}
            </div>
          </div>
        </div>
      )}

      {/* Lineup status */}
      {lineup && (
        <div style={{ padding: '6px 0', borderTop: `1px solid ${C.dim2}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.dim }}>Lineup Status</span>
            <span style={{
              fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 11,
              color: lineupColor, padding: '2px 8px', borderRadius: 4,
              border: `1px solid ${lineupColor}40`, background: `${lineupColor}18`,
            }}>{lineup}</span>
          </div>
          {game.intel?.lineup_note && (
            <p style={{ margin: '4px 0 0', fontSize: 10, color: C.dim, fontStyle: 'italic' }}>
              {game.intel.lineup_note}
            </p>
          )}
        </div>
      )}
    </Section>
  )
}

function OddsCell({ top, bottom }) {
  return (
    <div style={{
      background: C.bk3, border: `1px solid ${C.border}`,
      borderRadius: 6, padding: '5px 4px', textAlign: 'center',
    }}>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 12, color: C.text }}>{top}</div>
      {bottom && <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim, marginTop: 1 }}>{bottom}</div>}
    </div>
  )
}

function HeaderBar({ home, away, game }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8,
      alignItems: 'center', padding: '14px 16px',
      background: `linear-gradient(180deg, ${C.bk3}, ${C.bk2})`,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 22 }}>{away.abbreviation || away.name}</div>
        <Mono size={11} color={C.dim}>{away.record}</Mono>
      </div>
      <div style={{ textAlign: 'center' }}>
        <Mono size={10} color={C.text} bold>{formatET(game.commence_time)}</Mono>
        <div style={{ fontSize: 9, color: C.dim, marginTop: 2 }}>{game.status || ''}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 22 }}>{home.abbreviation || home.name}</div>
        <Mono size={11} color={C.dim}>{home.record}</Mono>
      </div>
    </div>
  )
}


function PitcherCardBlock({ pitchers }) {
  if (!pitchers || (!pitchers.home && !pitchers.away)) return null
  const a = pitchers.away, h = pitchers.home
  const Row = ({ label, av, hv }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr', gap: 4, padding: '5px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center' }}>
      <Mono size={11} color={C.text}>{av ?? '—'}</Mono>
      <div style={{ textAlign: 'center', fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>{label}</div>
      <Mono size={11} color={C.text}>{hv ?? '—'}</Mono>
    </div>
  )
  return (
    <Section title="Probable Pitchers">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{a?.name || 'TBD'}</div>
          <div style={{ fontSize: 9, color: C.dim }}>{a?.throws ? `${a.throws}HP` : '—'}</div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>VS</div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{h?.name || 'TBD'}</div>
          <div style={{ fontSize: 9, color: C.dim }}>{h?.throws ? `${h.throws}HP` : '—'}</div>
        </div>
      </div>
      <Row label="ERA"   av={a?.era}     hv={h?.era} />
      <Row label="FIP"   av={a?.fip}     hv={h?.fip} />
      <Row label="WHIP"  av={a?.whip}    hv={h?.whip} />
      <Row label="K/9"   av={a?.k_per_9} hv={h?.k_per_9} />
      <Row label="HR/9"  av={a?.hr9}     hv={h?.hr9} />
      <Row label="AVG IP" av={a?.avg_ip} hv={h?.avg_ip} />
    </Section>
  )
}

function LeaderRow({ awayP, homeP, label }) {
  return (
    <div style={{ padding: '8px 0', borderTop: `1px solid ${C.dim2}` }}>
      <div style={{ textAlign: 'center', fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.06em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'center' }}>
        <PlayerCell p={awayP} align="left" />
        <PlayerCell p={homeP} align="right" />
      </div>
    </div>
  )
}

function PlayerCell({ p, align }) {
  if (!p) return <div style={{ textAlign: align, color: C.dim, fontSize: 11 }}>—</div>
  const isLeft = align === 'left'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      flexDirection: isLeft ? 'row' : 'row-reverse',
      justifyContent: isLeft ? 'flex-start' : 'flex-end',
    }}>
      {p.headshot && (
        <img src={p.headshot} alt="" style={{
          width: 36, height: 36, borderRadius: '50%', background: C.bk3,
          objectFit: 'cover', border: `1px solid ${C.dim2}`,
        }} onError={(e) => { e.currentTarget.style.display = 'none' }} />
      )}
      <div style={{ textAlign: align, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.player_name}
        </div>
        <Mono size={11} color={C.blue}>{p.value}</Mono>
      </div>
    </div>
  )
}

function LeadersSection({ title, leaders, away, home }) {
  const cats = (leaders?.home || leaders?.away || [])
  if (!cats.length) return null
  // Index by category for paired display
  const byCat = (list) => Object.fromEntries((list || []).map(L => [L.category, L]))
  const ah = byCat(leaders.away), hh = byCat(leaders.home)
  const allCats = Array.from(new Set([
    ...(leaders.home || []).map(l => l.category),
    ...(leaders.away || []).map(l => l.category),
  ]))
  return (
    <Section title={title}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginBottom: 4 }}>
        <Mono size={10} color={C.dim}>{away.abbreviation}</Mono>
        <Mono size={10} color={C.dim} style={{ textAlign: 'right' }}>{home.abbreviation}</Mono>
      </div>
      {allCats.map(cat => (
        <LeaderRow key={cat}
          label={ah[cat]?.label || hh[cat]?.label || cat}
          awayP={ah[cat]} homeP={hh[cat]}
        />
      ))}
    </Section>
  )
}

function InjuryList({ team, list }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Mono size={11} color={C.text} bold>{team.abbreviation || team.name}</Mono>
      {(!list || list.length === 0) ? (
        <div style={{ fontSize: 10, color: C.dim, padding: '4px 0' }}>No injuries reported.</div>
      ) : list.map((p, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 8,
          padding: '6px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center',
        }}>
          <div>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{p.name}</span>{' '}
            <Mono size={10} color={C.dim}>{p.position}</Mono>
          </div>
          <Mono size={10} color={C.yellow || C.blue}>{p.status}</Mono>
        </div>
      ))}
    </div>
  )
}

function InjurySection({ injuries, home, away }) {
  return (
    <Section title="Injury Report">
      <InjuryList team={away} list={injuries.away} />
      <InjuryList team={home} list={injuries.home} />
    </Section>
  )
}

const HIT_FIELDS  = [
  ['avg', 'Batting Average'], ['runs', 'Runs'], ['hits', 'Hits'],
  ['homeRuns', 'Home Runs'], ['obp', 'On Base %'], ['slg', 'Slugging %'],
]
const PIT_FIELDS  = [
  ['era', 'Earned Run Average'], ['whip', 'WHIP'], ['baseOnBalls', 'Walks'],
  ['strikeOuts', 'Strikeouts'], ['avg', 'Opponent Batting Avg'],
]

function StatsSection({ team_stats, home, away }) {
  const ah = team_stats?.hitting?.away || {}, hh = team_stats?.hitting?.home || {}
  const ap = team_stats?.pitching?.away || {}, hp = team_stats?.pitching?.home || {}
  const Row = ({ label, av, hv }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 4, padding: '5px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center' }}>
      <Mono size={11} color={C.text}>{av ?? '—'}</Mono>
      <div style={{ textAlign: 'center', fontSize: 10, color: C.dim }}>{label}</div>
      <div style={{ textAlign: 'right' }}><Mono size={11} color={C.text}>{hv ?? '—'}</Mono></div>
    </div>
  )
  return (
    <Section title="Team Stats">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', marginBottom: 4 }}>
        <Mono size={10} color={C.dim}>{away.abbreviation}</Mono>
        <div />
        <div style={{ textAlign: 'right' }}><Mono size={10} color={C.dim}>{home.abbreviation}</Mono></div>
      </div>
      {HIT_FIELDS.map(([k, l]) => <Row key={`h-${k}`} label={l} av={ah[k]} hv={hh[k]} />)}
      {PIT_FIELDS.map(([k, l]) => <Row key={`p-${k}`} label={l} av={ap[k]} hv={hp[k]} />)}
    </Section>
  )
}

function RecentList({ team, list }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Mono size={11} color={C.text} bold>{team.abbreviation || team.name} · Last 5</Mono>
      {(!list || list.length === 0) ? (
        <div style={{ fontSize: 10, color: C.dim, padding: '4px 0' }}>No recent games.</div>
      ) : list.map((g, i) => {
        const isHome = g.home_id === team.id
        const us  = isHome ? g.home_score : g.away_score
        const them = isHome ? g.away_score : g.home_score
        const opp = isHome ? g.away_name  : g.home_name
        const result = us != null && them != null
          ? (us > them ? 'W' : us < them ? 'L' : 'T')
          : '—'
        const color = result === 'W' ? C.green : result === 'L' ? C.red || '#e34' : C.dim
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 8,
            padding: '6px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center',
          }}>
            <Mono size={11} color={color} bold>{result}</Mono>
            <div style={{ fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {isHome ? 'vs' : '@'} {opp}
            </div>
            <Mono size={11} color={C.dim}>{us}-{them}</Mono>
          </div>
        )
      })}
    </div>
  )
}

function RecentSection({ recent, home, away }) {
  return (
    <Section title="Recent Form">
      <RecentList team={away} list={recent.away} />
      <RecentList team={home} list={recent.home} />
    </Section>
  )
}

function SeriesSection({ series, home, away }) {
  if (!series || !series.games || series.games.length === 0) return null
  return (
    <Section title="Current Series" right={series.status || ''}>
      {series.games.map((g, i) => {
        const isFinal = g.status === 'Final'
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '60px 1fr auto', gap: 8,
            padding: '6px 0', borderTop: i === 0 ? 'none' : `1px solid ${C.dim2}`, alignItems: 'center',
          }}>
            <Mono size={10} color={C.dim}>Game {i + 1}</Mono>
            <div style={{ fontSize: 11 }}>
              {g.away_name} <span style={{ color: C.dim }}>@</span> {g.home_name}
            </div>
            <Mono size={11} color={isFinal ? C.text : C.dim}>
              {isFinal ? `${g.away_score}-${g.home_score}` : (g.status || 'TBD')}
            </Mono>
          </div>
        )
      })}
    </Section>
  )
}

function StandingsSection({ standings, home, away }) {
  if (!standings || standings.length === 0) return null
  const div = standings[0]?.div_name || 'Division'
  return (
    <Section title={div}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 30px 30px 50px 30px', gap: 4, padding: '4px 0', borderBottom: `1px solid ${C.dim2}` }}>
        <Mono size={9} color={C.dim}>TEAM</Mono>
        <Mono size={9} color={C.dim} style={{ textAlign: 'right' }}>W</Mono>
        <Mono size={9} color={C.dim} style={{ textAlign: 'right' }}>L</Mono>
        <Mono size={9} color={C.dim} style={{ textAlign: 'right' }}>PCT</Mono>
        <Mono size={9} color={C.dim} style={{ textAlign: 'right' }}>GB</Mono>
      </div>
      {standings.map((t) => {
        const highlighted = t.team_id === home.id || t.team_id === away.id
        return (
          <div key={t.team_id} style={{
            display: 'grid', gridTemplateColumns: '1fr 30px 30px 50px 30px', gap: 4,
            padding: '6px 0', borderTop: `1px solid ${C.dim2}`, alignItems: 'center',
          }}>
            <div style={{
              fontSize: 12, fontWeight: highlighted ? 800 : 500,
              color: highlighted ? C.blue : C.text,
            }}>{t.name}</div>
            <Mono size={11} color={C.text} style={{ textAlign: 'right' }}>{t.w}</Mono>
            <Mono size={11} color={C.text} style={{ textAlign: 'right' }}>{t.l}</Mono>
            <Mono size={11} color={C.text} style={{ textAlign: 'right' }}>{t.pct?.toFixed(3)}</Mono>
            <Mono size={11} color={C.dim} style={{ textAlign: 'right' }}>{t.gb}</Mono>
          </div>
        )
      })}
    </Section>
  )
}

const PROP_LABEL = {
  'HRR_over_1.5': 'HRR', 'HRR': 'HRR',
  'HR_anytime': 'HR', 'total_bases': 'TB',
  'strikeouts': 'K', 'doubles': '2B',
}

function fmtOdds(n) {
  if (n == null) return null
  return n > 0 ? `+${n}` : `${n}`
}

function EdgePill({ edge, noVig }) {
  const color = noVig ? C.yellow : C.green
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 700,
      color, background: `${color}18`, border: `1px solid ${color}40`,
      borderRadius: 4, padding: '1px 5px',
    }}>
      {noVig && '~'}{edge > 0 ? '+' : ''}{(edge * 100).toFixed(1)}%
    </span>
  )
}

function TopPlaysSection({ plays }) {
  if (!plays || plays.length === 0) return null
  return (
    <Section title="Top Plays" right={`${plays.length} edge${plays.length !== 1 ? 's' : ''}`}>
      {plays.map((p, i) => {
        const market = PROP_LABEL[p.prop_type] || p.prop_type
        const odds   = fmtOdds(p.book_over)
        const noVig  = p.warning_flags?.includes?.('no_vig_unavailable')
        const prob   = p.prob_over != null ? `${Math.round(p.prob_over * 100)}%` : null
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '1fr auto',
            gap: 8, padding: '8px 0',
            borderTop: `1px solid ${C.dim2}`, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 2, alignItems: 'center' }}>
                <span style={{
                  fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 11,
                  color: C.blue, background: `${C.blue}18`,
                  border: `1px solid ${C.blue}40`, borderRadius: 4, padding: '0 5px',
                }}>{market} o{p.line}</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim }}>
                  {p.team}
                </span>
                {odds && (
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim }}>
                    {odds}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              <EdgePill edge={p.edge_pct} noVig={noVig} />
              {prob && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim }}>{prob} proj</span>}
            </div>
          </div>
        )
      })}
    </Section>
  )
}

const LINEUP_BADGE_COLOR = {
  Confirmed: C.green,
  Projected: C.yellow,
  TBD:       C.dim,
}

function PropStat({ label, stat }) {
  if (!stat) return null
  const hasEdge = stat.edge > 3
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>{label}</span>
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: hasEdge ? 700 : 400,
        color: hasEdge ? C.green : C.dim,
      }}>
        {stat.prob}%{hasEdge ? ` ▲` : ''}
      </span>
    </div>
  )
}

function PlayerRow({ p, opposingHand }) {
  const props  = p.props   || {}
  const splits = p.splits  || {}
  const split  = opposingHand ? splits[opposingHand.toLowerCase()] : null
  return (
    <div style={{ padding: '7px 0', borderTop: `1px solid ${C.dim2}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: C.dim, width: 14, textAlign: 'right', flexShrink: 0 }}>
          {p.slot ?? '·'}
        </span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.name}
        </span>
        {p.form === 'hot'  && <span title="Hot — above-average recent TB rate"  style={{ fontSize: 11, lineHeight: 1 }}>🔥</span>}
        {p.form === 'cold' && <span title="Cold — below-average recent TB rate" style={{ fontSize: 11, lineHeight: 1 }}>❄️</span>}
        {p.bats && (
          <span style={{
            fontSize: 8, color: C.dim, background: C.bk, border: `1px solid ${C.dim2}`,
            borderRadius: 3, padding: '1px 4px', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, flexShrink: 0,
          }}>{p.bats}HB</span>
        )}
        {p.position && (
          <span style={{ fontSize: 9, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, flexShrink: 0 }}>
            {p.position}
          </span>
        )}
      </div>
      {(props.HRR || props.TB || props.HR || props.K) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 3, paddingLeft: 20 }}>
          <PropStat label="HRR" stat={props.HRR} />
          <PropStat label="TB"  stat={props.TB} />
          <PropStat label="HR"  stat={props.HR} />
          <PropStat label="K"   stat={props.K} />
        </div>
      )}
      {split && opposingHand && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, paddingLeft: 20 }}>
          <span style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>
            vs {opposingHand}HP
          </span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim }}>
            HRR {Math.round(split.hrr_rate * 100)}%
          </span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: C.dim }}>
            TB {split.tb_rate.toFixed(1)}
          </span>
          <span style={{ fontSize: 8, color: C.dim }}>({split.games}g)</span>
        </div>
      )}
    </div>
  )
}

function LineupSection({ lineup, home, away, lineupStatus, pitchers }) {
  if (!lineup || lineup.length === 0) return null
  const homeAbbr = home?.abbreviation
  const awayAbbr = away?.abbreviation

  const homeGroup = lineup.filter(p => p.team === homeAbbr)
  const awayGroup = lineup.filter(p => p.team === awayAbbr)
  const other     = lineup.filter(p => p.team !== homeAbbr && p.team !== awayAbbr)

  // Home batters face away starter; away batters face home starter
  const homeOpposingHand = pitchers?.away?.throws || null
  const awayOpposingHand = pitchers?.home?.throws || null

  const slottedCount = lineup.filter(p => p.slot != null).length
  const statusColor  = LINEUP_BADGE_COLOR[lineupStatus] || C.dim

  const TeamGroup = ({ abbr, players, opposingHand }) => {
    if (!players || players.length === 0) return null
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: 12, color: C.text }}>{abbr}</span>
          <span style={{ fontSize: 9, color: C.dim }}>{players.length} players projected</span>
          {opposingHand && <span style={{ fontSize: 8, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700 }}>vs {opposingHand}HP</span>}
        </div>
        {players.map((p, i) => <PlayerRow key={i} p={p} opposingHand={opposingHand} />)}
      </div>
    )
  }

  return (
    <Section
      title="Lineup"
      right={slottedCount > 0 ? `${slottedCount} confirmed` : lineupStatus}
    >
      {lineupStatus && lineupStatus !== 'Confirmed' && (
        <div style={{ fontSize: 10, color: statusColor, marginBottom: 8, fontStyle: 'italic' }}>
          {lineupStatus === 'Projected' ? 'Lineups not yet confirmed — showing probable starters' : 'Lineup TBD'}
        </div>
      )}
      <TeamGroup abbr={awayAbbr} players={awayGroup} opposingHand={awayOpposingHand} />
      <TeamGroup abbr={homeAbbr} players={homeGroup} opposingHand={homeOpposingHand} />
      {other.length > 0 && <TeamGroup abbr="Other" players={other} opposingHand={null} />}
    </Section>
  )
}

const GRADE_ROWS = [
  { key: 'edges',       label: 'Edges',    max: 30 },
  { key: 'lineup',      label: 'Lineup',   max: 20 },
  { key: 'pitcher',     label: 'Pitcher',  max: 15 },
  { key: 'park_weather',label: 'Park/Wx',  max: 15 },
  { key: 'bullpen',     label: 'Bullpen',  max: 10 },
  { key: 'odds',        label: 'Odds',     max: 10 },
]

function gradeColor(pct) {
  if (pct >= 0.75) return C.green
  if (pct >= 0.50) return C.blue
  if (pct >= 0.30) return C.yellow
  return C.dim
}

function GradeBreakdown({ intel }) {
  const gc = intel?.grade_components
  if (!gc) return null
  return (
    <Section title="Grade Breakdown" right={`${intel.grade}/100`}>
      {GRADE_ROWS.map(({ key, label, max }) => {
        const val = gc[key] ?? 0
        const pct = max > 0 ? val / max : 0
        const color = gradeColor(pct)
        return (
          <div key={key} style={{ padding: '5px 0', borderTop: `1px solid ${C.dim2}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: C.dim, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '.04em', width: 60 }}>{label}</span>
              <div style={{
                flex: 1, height: 5, background: C.dim2, borderRadius: 3,
                margin: '0 8px', overflow: 'hidden',
              }}>
                <div style={{
                  width: `${Math.round(pct * 100)}%`,
                  height: '100%',
                  background: color,
                  borderRadius: 3,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: color, fontWeight: 700, width: 32, textAlign: 'right' }}>
                {val}/{max}
              </span>
            </div>
          </div>
        )
      })}
    </Section>
  )
}

export default function GameDetailDrawer({ game, onClose }) {
  const { data, loading, error } = useFetch(
    () => getGameDetail(game.home.id, game.away.id, game.commence_time?.slice(0, 10) || ''),
    [game.id]
  )

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bk, width: '100%', maxWidth: 430,
          maxHeight: '92vh', overflowY: 'auto',
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          border: `1px solid ${C.border}`,
          paddingBottom: 24,
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 14px', borderBottom: `1px solid ${C.dim2}`,
        }}>
          <Mono size={10} color={C.dim}>GAME DETAIL</Mono>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: C.dim, fontSize: 20,
            cursor: 'pointer', lineHeight: 1, padding: 4,
          }}>×</button>
        </div>

        <HeaderBar home={game.home} away={game.away} game={game} />

        <GameSnapshotSection game={game} />
        <GradeBreakdown intel={game.intel} />
        <TopPlaysSection plays={game.plays} />
        <LineupSection lineup={game.lineup} home={game.home} away={game.away} lineupStatus={game.intel?.lineup_status} pitchers={game.pitchers} />
        <PitcherCardBlock pitchers={game.pitchers} />

        {loading && <Spinner />}
        {error && <Empty message="Could not load detail" />}
        {!loading && !error && data && (
          <>
            <LeadersSection title="Batting Leaders" leaders={data.leaders?.hitting} home={data.home} away={data.away} />
            <LeadersSection title="Pitching Leaders" leaders={data.leaders?.pitching} home={data.home} away={data.away} />
            <InjurySection injuries={data.injuries || {}} home={data.home} away={data.away} />
            <StatsSection team_stats={data.team_stats || {}} home={data.home} away={data.away} />
            <RecentSection recent={data.recent || {}} home={data.home} away={data.away} />
            <SeriesSection series={data.series} home={data.home} away={data.away} />
            <StandingsSection standings={data.standings} home={data.home} away={data.away} />
          </>
        )}
      </div>
    </div>
  )
}
